import React, { PropsWithChildren, ReactNode } from 'react'
import clsx from 'clsx'
import { AnimatePresence, motion } from 'framer-motion'
import uniqueId from 'lodash.uniqueid'
import { Collapsible as CollapsibleStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useControlled from '../../lib/hooks/useControlled'
import useDeprecatedComponent from '../../lib/hooks/useDeprecatedComponent'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** Collapsible expands to the maximum height available */
  autoFit?: boolean
  /** Default expanded state (when uncontrolled) */
  defaultExpanded?: boolean
  /** If `true`, component will be disabled */
  disabled?: boolean
  /** If `true`, the content is expanded */
  expanded?: boolean
  /**
   * Heading level to adapt to your page structure
   * @default h2
   */
  heading?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /** Handler called when expanded state changes */
  onChange?: (event: React.MouseEvent<HTMLButtonElement>, expanded: boolean) => void
  /**
   * Collapsible variant
   * @default accordion
   */
  variant?: 'accordion' | 'filterpanel'
  /** Title displayed when collapsed */
  title: ReactNode
}>

export type CollapsibleProps = Props & CommonProps

const useStyles = createUseStyles(CollapsibleStyles, 'collapsible')

const Collapsible = (props: CollapsibleProps) => {
  const {
    autoFit,
    children,
    className,
    defaultExpanded,
    disabled,
    expanded: expandedProp,
    heading: HeadingComponent = 'h2',
    id: idProp,
    onChange,
    style,
    title,
    variant,
    ...rest
  } = props
  const classes = useStyles()

  // unique ids
  const headerId: string = React.useMemo(() => idProp || uniqueId('Collapsible-header-'), [idProp])
  const contentId: string = React.useMemo(() => uniqueId('Collapsible-content-'), [])

  // controlled / uncontrolled value
  const [expanded, setExpanded] = useControlled({
    value: expandedProp,
    defaultValue: defaultExpanded,
  })

  const handleChange = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExpanded(!expanded)
    onChange && onChange(event, !expanded)
  }

  // FIXME this may not be useful anymore with latest framer motion releases
  // When content is expanded, it should be possible to scroll if it exceeds fixed height
  // But we want to hide scrollbars during animation
  // Current framer motion library version does not manage this very well
  // This approach also fix layout issue with vite/react18.2
  // @see https://github.airbus.corp/Airbus/design-system/issues/1590
  const divRef = React.useRef<HTMLDivElement>(null)
  const onAnimationUpdate = React.useCallback((latest: any) => {
    if (latest.opacity < 1 && latest.opacity > 0) {
      if (divRef.current) divRef.current.style.overflowY = 'hidden'
    }
  }, [])
  const onAnimationStart = React.useCallback(() => {
    if (divRef.current) divRef.current.style.overflowY = 'hidden'
  }, [])
  const onAnimationComplete = React.useCallback((def: string) => {
    if (divRef.current) divRef.current.style.overflowY = def === 'expanded' ? 'auto' : 'hidden'
  }, [])

  return (
    <div
      className={clsx(className, classes.root, {
        [classes._filterpanel]: variant === 'filterpanel',
        [classes._expanded]: expanded,
        [classes._disabled]: disabled,
        [classes._autofit]: autoFit && expanded,
      })}
      style={style}
    >
      <HeadingComponent className={classes.header} {...rest}>
        <button
          className={classes.button}
          aria-controls={expanded && contentId}
          aria-disabled={disabled}
          aria-expanded={expanded}
          id={headerId}
          onClick={handleChange}
        >
          {title}
        </button>
      </HeadingComponent>
      <AnimatePresence initial={false}>
        {expanded && (
          <motion.div
            id={contentId}
            ref={divRef}
            role='region'
            aria-labelledby={headerId}
            key='content'
            initial='collapsed'
            animate='expanded'
            exit='collapsed'
            variants={{
              collapsed: { opacity: 0, height: 0 },
              expanded: { opacity: 1, height: 'auto' },
            }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            onAnimationStart={onAnimationStart}
            onUpdate={onAnimationUpdate}
            onAnimationComplete={onAnimationComplete}
            style={{
              display: 'flex',
              flexDirection: 'column',
              overflowY: 'auto',
            }}
          >
            <div className={classes.content} style={{ overflowY: 'unset' }}>
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default Collapsible

// Retro compatibility with Panel component
export const Panel = (props: CollapsibleProps) => {
  useDeprecatedComponent('Panel', 'Please use Collapsible.')
  return <Collapsible {...props} />
}
